import React, { useState, useEffect } from "react";
import { Modal, Grow, IconButton, Button, Checkbox } from "@mui/material";
import { Spin, message, Select } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { Close, PictureAsPdf, TableView } from "@mui/icons-material";
import { createReportJob } from "Explore/Services/Report";
import { setUnseenExportCount } from "Redux/Slices/Common";
import styles from "./AddToReportModal.module.scss";
import {
  COLLABORATION_REPORT_SECTIONS,
  ANALYSIS_REPORT_SECTIONS,
  ANALYSIS_METRIC_META,
  ANALYSIS_METRIC_CATEGORIES,
  ANALYSIS_TREND_METRICS,
  ANALYSIS_RADAR_METRICS,
  ANALYSIS_SCATTER_Y_METRICS,
  ANALYSIS_SCATTER_Z_METRICS,
  ANALYSIS_QUALITY_GROUPS,
  ANALYSIS_RANKING_METRICS,
} from "Explore/Config/constraint";

const DEFAULT_ANALYSIS_XLSX_METRICS = [
  "total_pubs",
  "pubs_per_faculty",
  "total_citations",
  "citations_per_paper",
  "h_index",
  "total_grant_amount",
];

const AddToReportModal = ({
  open,
  onClose,
  entity,
  entities = [],
  entityType,
  filters,
  exportFormat = "pdf",
  module = "collaboration",
  sortBy = "total_pubs",
  sortOrder = "desc",
  isPinned = false,
  topN = 5,
}) => {
  const [reportTitle, setReportTitle] = useState("");
  const [selectedSections, setSelectedSections] = useState({});
  const [selectedMetrics, setSelectedMetrics] = useState(DEFAULT_ANALYSIS_XLSX_METRICS);
  const [isGenerating, setIsGenerating] = useState(false);
  const [animatingReport, setAnimatingReport] = useState(false);
  const [animStyle, setAnimStyle] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const generateBtnRef = React.useRef(null);
  const user = useSelector((state) => state.user?.data);
  const unseenExportCount = useSelector(state => state.commonState?.unseenExportCount || 0);
  const dispatch = useDispatch();

  const isAnalysisXlsx = module === "analysis" && exportFormat === "xlsx";

  const [visualOptions, setVisualOptions] = useState({
    trend_metrics: ["total_pubs"],
    growth_metrics: ["total_pubs"],
    radar_metrics: [
      "total_pubs",
      "total_citations",
      "h_index",
      "total_grant_amount",
      "int_collab",
    ],
    scatter_y_metrics: ["median_citations_per_paper"],
    scatter_z: "total_grant_amount",
    quality_groups: ["groupA"],
    ranking_metrics: ["total_pubs"],
  });

  useEffect(() => {
    if (animatingReport && generateBtnRef.current) {
      const endEl = document.getElementById("sidebar-export-center-link");
      let endRect = { top: window.innerHeight / 2, left: 0, width: 40, height: 40 };
      if (endEl) {
        endRect = endEl.getBoundingClientRect();
      }

      setAnimStyle({
        position: 'fixed',
        top: window.innerHeight / 2 - 40,
        left: window.innerWidth / 2 - 30,
        width: 90,
        height: 120,
        transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        zIndex: 9999,
        opacity: 0,
        transform: 'scale(0.5) translateY(20px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimStyle(prev => ({
            ...prev,
            opacity: 1,
            transform: 'scale(1) translateY(0)',
          }));

          setTimeout(() => {
            setAnimStyle(prev => ({
              ...prev,
              top: endRect.top + endRect.height / 2 - 40,
              left: endRect.left + endRect.width / 2 - 30,
              opacity: 0,
              transform: 'scale(0.5) rotate(-15deg)',
              transition: 'all 1.6s cubic-bezier(0.4, 0, 0.2, 1)',
            }));
          }, 800);
        });
      });
    } else {
      setAnimStyle(null);
    }
  }, [animatingReport]);

  const allSections = module === "analysis" ? ANALYSIS_REPORT_SECTIONS : COLLABORATION_REPORT_SECTIONS;

  const availableSections = React.useMemo(() => {
    return allSections.filter((s) => {
      if (exportFormat === "xlsx") {
        const allowedXlsxKeys = module === "analysis"
          ? [
              "table_metrics",
              "visual_trend",
              "visual_growth",
              "visual_radar",
              "visual_scatter",
              "visual_quality",
              "visual_ranking",
            ]
          : [
              "general_trends",
              "general_reach",
              "general_subjects",
              "current_table",
              "potential_table"
            ];
        if (!allowedXlsxKeys.includes(s.key)) return false;
      }
      if (module === "collaboration" && entity?.type === "institution" && s.tab === "potential") {
        return false;
      }
      return true;
    });
  }, [exportFormat, entity, module, allSections]);

  useEffect(() => {
    if (open) {
      const isAnalysis = module === "analysis";
      const titlePrefix = exportFormat === "xlsx"
        ? "XLSX Export"
        : (isAnalysis ? "Benchmarking Analysis Report" : "Collaboration Report");

      const entityName = (entities && entities.length > 0)
        ? (entities.length === 1 ? entities[0].name : `${entities[0].name} & ${entities.length - 1} others`)
        : (entity?.name || "Benchmark Overview");

      setReportTitle(`${titlePrefix} - ${entityName}`);

      const initialSelection = {};
      availableSections.forEach((sec) => {
        initialSelection[sec.key] = true;
      });
      setSelectedSections(initialSelection);
    }
  }, [open, entity, entities, exportFormat, availableSections, module]);

  const allAnalysisMetricKeys = React.useMemo(() => {
    return ANALYSIS_METRIC_CATEGORIES.flatMap((c) => c.metrics);
  }, []);

  const handleMetricToggle = (metricKey) => {
    setSelectedMetrics((prev) =>
      prev.includes(metricKey)
        ? prev.filter((k) => k !== metricKey)
        : [...prev, metricKey]
    );
  };

  const handleCategoryToggle = (category) => {
    const catMetrics = category.metrics;
    const allChecked = catMetrics.every((m) => selectedMetrics.includes(m));
    setSelectedMetrics((prev) => {
      if (allChecked) {
        return prev.filter((m) => !catMetrics.includes(m));
      }
      const toAdd = catMetrics.filter((m) => !prev.includes(m));
      return [...prev, ...toAdd];
    });
  };

  const isCategoryAllChecked = (category) => {
    return category.metrics.every((m) => selectedMetrics.includes(m));
  };

  const isCategoryIndeterminate = (category) => {
    const checked = category.metrics.filter((m) => selectedMetrics.includes(m));
    return checked.length > 0 && checked.length < category.metrics.length;
  };

  const handleSelectAllMetrics = () => {
    setSelectedMetrics([...allAnalysisMetricKeys]);
  };

  const handleDeselectAllMetrics = () => {
    setSelectedMetrics([]);
  };

  const handleResetDefaultMetrics = () => {
    setSelectedMetrics([...DEFAULT_ANALYSIS_XLSX_METRICS]);
  };

  const handleSectionToggle = (key) => {
    setSelectedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGroupToggle = (tab) => {
    const groupKeys = availableSections.filter((s) => s.tab === tab).map((s) => s.key);
    const allChecked = groupKeys.every((k) => selectedSections[k]);
    setSelectedSections((prev) => {
      const updated = { ...prev };
      groupKeys.forEach((k) => { updated[k] = !allChecked; });
      return updated;
    });
  };

  const hasSelection = isAnalysisXlsx
    ? selectedMetrics.length > 0
    : Object.values(selectedSections).some((v) => v);

  const subjectAreas = filters?.subjectArea || [];
  const hasSubjectAreaViolation = module === "analysis" && subjectAreas.length > 1;
  const hasEntitiesViolation = module === "analysis" && entities.length > 25;

  const handleGenerate = async (format) => {
    if (!hasSelection) {
      if (isAnalysisXlsx) {
        messageApi.warning("Please select at least one metric to export to XLSX.");
      }
      return;
    }
    if (hasSubjectAreaViolation) {
      messageApi.error("Only 1 subject area is supported for export.");
      return;
    }
    if (hasEntitiesViolation) {
      messageApi.error("Maximum 25 entities can be exported.");
      return;
    }

    // Validate visual metric selections for analysis module (PDF only)
    if (module === "analysis" && format !== "xlsx") {
      if (selectedSections.visual_trend && (!visualOptions.trend_metrics || visualOptions.trend_metrics.length === 0)) {
        messageApi.warning("Please select at least one metric for Trend Evolution.");
        return;
      }
      if (selectedSections.visual_growth && (!visualOptions.growth_metrics || visualOptions.growth_metrics.length === 0)) {
        messageApi.warning("Please select at least one metric for Growth Momentum.");
        return;
      }
      if (selectedSections.visual_radar && (!visualOptions.radar_metrics || visualOptions.radar_metrics.length < 3 || visualOptions.radar_metrics.length > 10)) {
        messageApi.warning("Radar chart requires between 3 and 10 dimensions.");
        return;
      }
      if (selectedSections.visual_scatter && (!visualOptions.scatter_y_metrics || visualOptions.scatter_y_metrics.length === 0)) {
        messageApi.warning("Please select at least one Y-axis metric for Scatter Chart.");
        return;
      }
      if (selectedSections.visual_quality && (!visualOptions.quality_groups || visualOptions.quality_groups.length === 0)) {
        messageApi.warning("Please select at least one quality indicator group.");
        return;
      }
      if (selectedSections.visual_ranking && (!visualOptions.ranking_metrics || visualOptions.ranking_metrics.length === 0)) {
        messageApi.warning("Please select at least one indicator for Peer Ranking.");
        return;
      }
    }

    setIsGenerating(true);

    const selectedKeys = Object.entries(selectedSections)
      .filter(([, v]) => v)
      .map(([k]) => k);

    const currentUserId = user?.id || user?._id || user?.username || "current_user";

    const visualOptionsPayload = (module === "analysis" && format !== "xlsx") ? {
      trend_metrics: visualOptions.trend_metrics,
      growth_metrics: visualOptions.growth_metrics,
      radar_metrics: visualOptions.radar_metrics,
      scatter: {
        x: "total_pubs",
        y_metrics: visualOptions.scatter_y_metrics,
        z: visualOptions.scatter_z,
      },
      quality_groups: visualOptions.quality_groups,
      ranking_metrics: visualOptions.ranking_metrics,
    } : null;

    try {
      await createReportJob({
        userId: currentUserId,
        entity,
        entities,
        entityType,
        filters,
        sections: selectedKeys,
        metrics: selectedMetrics,
        title: reportTitle,
        format,
        module,
        visualOptions: visualOptionsPayload,
        sortBy,
        sortOrder,
      });

      setAnimatingReport(true);
      onClose();
      messageApi.success(`${format.toUpperCase()} job created! You can view it in My Library.`);
      dispatch(setUnseenExportCount(unseenExportCount + 1));
      setTimeout(() => {
        setAnimatingReport(false);
        setIsGenerating(false);
      }, 2000);
    } catch (error) {
      setIsGenerating(false);
      console.error("Error starting report generation:", error);
      let detail = "Failed to request generation.";
      if (error.response?.data?.detail) {
        detail = typeof error.response.data.detail === "string" 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        detail = error.message;
      }
      messageApi.error(`Failed to request ${format.toUpperCase()} generation: ${detail}`);
    }
  };

  // Group definitions depending on module
  const groupDefinitions = React.useMemo(() => {
    if (module === "analysis") {
      return [
        { tab: "table", label: "Table View" },
        { tab: "visual", label: "Visual View" },
      ];
    }
    return [
      { tab: "general", label: "General Overview" },
      { tab: "current", label: "Current Collaborations" },
      { tab: "potential", label: "Potential Collaborations" },
    ];
  }, [module]);

  const groupedSections = React.useMemo(() => {
    const map = {};
    groupDefinitions.forEach((g) => {
      map[g.tab] = availableSections.filter((s) => s.tab === g.tab);
    });
    return map;
  }, [groupDefinitions, availableSections]);

  const isGroupAllChecked = (tab) => {
    const groupKeys = availableSections.filter((s) => s.tab === tab).map((s) => s.key);
    if (groupKeys.length === 0) return false;
    return groupKeys.every((k) => selectedSections[k]);
  };

  const isGroupIndeterminate = (tab) => {
    const groupKeys = availableSections.filter((s) => s.tab === tab).map((s) => s.key);
    const checked = groupKeys.filter((k) => selectedSections[k]);
    return checked.length > 0 && checked.length < groupKeys.length;
  };

  const getSelectPopupContainer = (triggerNode) => {
    return (
      document.getElementById("add-to-report-modal-body") ||
      triggerNode?.parentNode ||
      document.body
    );
  };

  const renderVisualMetricSelector = (secKey) => {
    switch (secKey) {
      case "visual_trend":
        return (
          <div className={styles["metric-selector-box"]}>
            <div className={styles["metric-header-row"]}>
              <span className={styles["metric-label"]}>Trend Metrics:</span>
              <span className={`${styles["metric-badge"]} ${visualOptions.trend_metrics.length === 0 ? styles.warning : ""}`}>
                {visualOptions.trend_metrics.length} selected ({visualOptions.trend_metrics.length} chart{visualOptions.trend_metrics.length === 1 ? "" : "s"})
              </span>
            </div>
            <Select
              mode="multiple"
              size="small"
              style={{ width: "100%" }}
              maxTagCount="responsive"
              value={visualOptions.trend_metrics}
              onChange={(vals) => setVisualOptions((prev) => ({ ...prev, trend_metrics: vals }))}
              options={ANALYSIS_TREND_METRICS}
              placeholder="Select trend metrics..."
              dropdownStyle={{ zIndex: 99999 }}
              getPopupContainer={getSelectPopupContainer}
            />
            <span className={styles["metric-helper-note"]}>
              Each selected metric will be exported as an individual Trend Line Chart.
            </span>
          </div>
        );
      case "visual_growth":
        return (
          <div className={styles["metric-selector-box"]}>
            <div className={styles["metric-header-row"]}>
              <span className={styles["metric-label"]}>Growth Metrics:</span>
              <span className={`${styles["metric-badge"]} ${visualOptions.growth_metrics.length === 0 ? styles.warning : ""}`}>
                {visualOptions.growth_metrics.length} selected ({visualOptions.growth_metrics.length} chart{visualOptions.growth_metrics.length === 1 ? "" : "s"})
              </span>
            </div>
            <Select
              mode="multiple"
              size="small"
              style={{ width: "100%" }}
              maxTagCount="responsive"
              value={visualOptions.growth_metrics}
              onChange={(vals) => setVisualOptions((prev) => ({ ...prev, growth_metrics: vals }))}
              options={ANALYSIS_TREND_METRICS}
              placeholder="Select growth metrics..."
              dropdownStyle={{ zIndex: 99999 }}
              getPopupContainer={getSelectPopupContainer}
            />
            <span className={styles["metric-helper-note"]}>
              Each selected metric will be exported as an individual Growth Bar Chart.
            </span>
          </div>
        );
      case "visual_radar":
        return (
          <div className={styles["metric-selector-box"]}>
            <div className={styles["metric-header-row"]}>
              <span className={styles["metric-label"]}>Radar Dimensions (Min 3):</span>
              <span className={`${styles["metric-badge"]} ${visualOptions.radar_metrics.length < 3 ? styles.warning : ""}`}>
                {visualOptions.radar_metrics.length} selected
              </span>
            </div>
            <Select
              mode="multiple"
              size="small"
              style={{ width: "100%" }}
              maxTagCount="responsive"
              value={visualOptions.radar_metrics}
              onChange={(vals) => {
                if (vals.length >= 3) {
                  setVisualOptions((prev) => ({ ...prev, radar_metrics: vals }));
                } else {
                  messageApi.warning("Radar chart requires at least 3 dimensions to form a polygon.");
                }
              }}
              options={ANALYSIS_RADAR_METRICS}
              placeholder="Select dimensions (min 3)..."
              dropdownStyle={{ zIndex: 99999 }}
              getPopupContainer={getSelectPopupContainer}
            />
            <span className={styles["metric-helper-note"]}>
              Dimensions define the multi-axis polygon for the Strength Profile chart.
            </span>
          </div>
        );
      case "visual_scatter":
        return (
          <div className={styles["metric-selector-box"]}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span className={styles["metric-label"]} style={{ margin: 0 }}>X-Axis (Volume):</span>
                <span style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>Total Publications (Fixed)</span>
              </div>
              <div>
                <div className={styles["metric-header-row"]}>
                  <span className={styles["metric-label"]}>Y-Axis Quality Metrics:</span>
                  <span className={`${styles["metric-badge"]} ${visualOptions.scatter_y_metrics.length === 0 ? styles.warning : ""}`}>
                    {visualOptions.scatter_y_metrics.length} selected ({visualOptions.scatter_y_metrics.length} chart{visualOptions.scatter_y_metrics.length === 1 ? "" : "s"})
                  </span>
                </div>
                <Select
                  mode="multiple"
                  size="small"
                  style={{ width: "100%" }}
                  maxTagCount="responsive"
                  value={visualOptions.scatter_y_metrics}
                  onChange={(vals) => setVisualOptions((prev) => ({ ...prev, scatter_y_metrics: vals }))}
                  options={ANALYSIS_SCATTER_Y_METRICS}
                  placeholder="Select Y-axis metrics..."
                  dropdownStyle={{ zIndex: 99999 }}
                  getPopupContainer={getSelectPopupContainer}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span className={styles["metric-label"]} style={{ margin: 0 }}>Bubble Size (Scale):</span>
                <Select
                  size="small"
                  style={{ width: 230 }}
                  value={visualOptions.scatter_z}
                  onChange={(val) => setVisualOptions((prev) => ({ ...prev, scatter_z: val }))}
                  options={ANALYSIS_SCATTER_Z_METRICS}
                  dropdownStyle={{ zIndex: 99999 }}
                  getPopupContainer={getSelectPopupContainer}
                />
              </div>
              <span className={styles["metric-helper-note"]}>
                Each selected Y-axis metric will be exported as an individual Scatter Chart.
              </span>
            </div>
          </div>
        );
      case "visual_quality":
        return (
          <div className={styles["metric-selector-box"]}>
            <div className={styles["metric-header-row"]}>
              <span className={styles["metric-label"]}>Quality Indicator Groups:</span>
              <span className={`${styles["metric-badge"]} ${visualOptions.quality_groups.length === 0 ? styles.warning : ""}`}>
                {visualOptions.quality_groups.length} selected ({visualOptions.quality_groups.length} chart{visualOptions.quality_groups.length === 1 ? "" : "s"})
              </span>
            </div>
            <Select
              mode="multiple"
              size="small"
              style={{ width: "100%" }}
              maxTagCount="responsive"
              value={visualOptions.quality_groups}
              onChange={(vals) => setVisualOptions((prev) => ({ ...prev, quality_groups: vals }))}
              options={ANALYSIS_QUALITY_GROUPS}
              placeholder="Select quality indicator groups..."
              dropdownStyle={{ zIndex: 99999 }}
              getPopupContainer={getSelectPopupContainer}
            />
            <span className={styles["metric-helper-note"]}>
              Each selected group will be exported as an individual Grouped Bar Chart.
            </span>
          </div>
        );
      case "visual_ranking":
        return (
          <div className={styles["metric-selector-box"]}>
            <div className={styles["metric-header-row"]}>
              <span className={styles["metric-label"]}>Ranking Indicators:</span>
              <span className={`${styles["metric-badge"]} ${visualOptions.ranking_metrics.length === 0 ? styles.warning : ""}`}>
                {visualOptions.ranking_metrics.length} selected ({visualOptions.ranking_metrics.length} chart{visualOptions.ranking_metrics.length === 1 ? "" : "s"})
              </span>
            </div>
            <Select
              mode="multiple"
              size="small"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
              maxTagCount="responsive"
              value={visualOptions.ranking_metrics}
              onChange={(vals) => setVisualOptions((prev) => ({ ...prev, ranking_metrics: vals }))}
              options={ANALYSIS_RANKING_METRICS}
              placeholder="Select ranking indicators..."
              dropdownStyle={{ zIndex: 99999 }}
              getPopupContainer={getSelectPopupContainer}
            />
            <span className={styles["metric-helper-note"]}>
              Each selected indicator will be exported as an individual Horizontal Bar Chart.
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={isGenerating ? undefined : onClose}
        disableEnforceFocus
        disableAutoFocus
        sx={{
          zIndex: 1301,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Grow in={open}>
          <div className={styles["add-to-report-modal"]}>
            <div className={styles["add-to-report-modal__header"]}>
              <p>{exportFormat === "xlsx" ? "Export to XLSX" : "Add to Report"}</p>
              <IconButton onClick={isGenerating ? undefined : onClose} disabled={isGenerating}>
                <Close />
              </IconButton>
            </div>

            <div id="add-to-report-modal-body" className={styles["add-to-report-modal__body"]}>
              {hasSubjectAreaViolation && (
                <div style={{ padding: "8px 12px", marginBottom: "12px", borderRadius: "6px", backgroundColor: "#fff2f0", border: "1px solid #ffccc7", color: "#cf1322", fontSize: "13px" }}>
                  <strong>Filter limitation:</strong> Export currently supports maximum 1 subject area (selected: {subjectAreas.length}). Please remove extra subject areas in the filter panel before exporting.
                </div>
              )}

              {hasEntitiesViolation && (
                <div style={{ padding: "8px 12px", marginBottom: "12px", borderRadius: "6px", backgroundColor: "#fff2f0", border: "1px solid #ffccc7", color: "#cf1322", fontSize: "13px" }}>
                  <strong>Entity limit exceeded:</strong> Maximum 25 entities can be exported at once (selected: {entities.length}). Please adjust your filters or pinned rows.
                </div>
              )}

              {module === "analysis" && !hasSubjectAreaViolation && !hasEntitiesViolation && (
                <div style={{ padding: "6px 12px", marginBottom: "12px", borderRadius: "6px", backgroundColor: "#f0f5ff", border: "1px solid #d6e4ff", color: "#1d39c4", fontSize: "12px" }}>
                  {entities.length > 0 ? (
                    <span>Exporting <strong>{entities.length}</strong> {entityType || 'institution'}(s) ({isPinned ? 'pinned rows' : `top ${entities.length} in table`}).</span>
                  ) : (
                    <span>No specific entities selected. Backend will evaluate Top 5 {entityType || 'institution'}s automatically.</span>
                  )}
                </div>
              )}

              <div className={styles["report-title-input"]}>
                <label>{exportFormat === "xlsx" ? "Export File Name" : "Report Title"}</label>
                <input
                  type="text"
                  value={reportTitle}
                  onChange={(e) => setReportTitle(e.target.value)}
                  disabled={isGenerating}
                />
              </div>

              {isAnalysisXlsx ? (
                <div className={styles["metrics-selection"]}>
                  <div className={styles["metrics-selection__header"]}>
                    <div className={styles["header-title-box"]}>
                      <label>Select Metrics to Export:</label>
                      <span className={styles["metric-counter"]}>
                        ({selectedMetrics.length} of {allAnalysisMetricKeys.length} selected)
                      </span>
                    </div>
                    <div className={styles["metrics-actions"]}>
                      <button type="button" onClick={handleSelectAllMetrics} disabled={isGenerating}>
                        Select All
                      </button>
                      <button type="button" onClick={handleDeselectAllMetrics} disabled={isGenerating}>
                        Deselect All
                      </button>
                      <button type="button" onClick={handleResetDefaultMetrics} disabled={isGenerating}>
                        Default (6)
                      </button>
                    </div>
                  </div>

                  <div className={styles["metrics-groups"]}>
                    {ANALYSIS_METRIC_CATEGORIES.map((category) => {
                      const checkedCount = category.metrics.filter((m) => selectedMetrics.includes(m)).length;
                      return (
                        <div key={category.key} className={styles["metric-group"]}>
                          <div className={styles["metric-group__header"]}>
                            <div className={styles["group-title-area"]}>
                              <Checkbox
                                checked={isCategoryAllChecked(category)}
                                indeterminate={isCategoryIndeterminate(category)}
                                onChange={() => handleCategoryToggle(category)}
                                disabled={isGenerating}
                                size="small"
                              />
                              <h4>{category.label}</h4>
                            </div>
                            <span className={styles["group-count"]}>
                              {checkedCount} / {category.metrics.length} selected
                            </span>
                          </div>

                          <div className={styles["metric-items-grid"]}>
                            {category.metrics.map((metricKey) => {
                              const isChecked = selectedMetrics.includes(metricKey);
                              const meta = ANALYSIS_METRIC_META[metricKey] || { label: metricKey, type: "count" };
                              return (
                                <label key={metricKey} className={styles["metric-checkbox-item"]}>
                                  <div className={styles["metric-checkbox-left"]}>
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={() => handleMetricToggle(metricKey)}
                                      disabled={isGenerating}
                                      size="small"
                                    />
                                    <span title={meta.label}>{meta.label}</span>
                                  </div>
                                  <span className={`${styles["metric-type-pill"]} ${styles[`type-${meta.type || 'count'}`]}`}>
                                    {meta.type || "count"}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className={styles["sections-selection"]}>
                  <label>Select content to include:</label>

                  <div className={styles["section-groups"]}>
                    {groupDefinitions
                      .map((group) => ({ ...group, sections: groupedSections[group.tab] }))
                      .filter((group) => group.sections?.length > 0)
                      .map(({ tab, label, sections }) => (
                      <div key={tab} className={styles["section-group"]}>
                        <div className={styles["section-group__header"]}>
                          <Checkbox
                            checked={isGroupAllChecked(tab)}
                            indeterminate={isGroupIndeterminate(tab)}
                            onChange={() => handleGroupToggle(tab)}
                            disabled={isGenerating}
                            size="small"
                          />
                          <h4>{label}</h4>
                        </div>
                        {sections.map((sec) => (
                          <div key={sec.key} className={styles["section-item-wrapper"]}>
                            <label className={styles["checkbox-label"]}>
                              <Checkbox
                                checked={!!selectedSections[sec.key]}
                                onChange={() => handleSectionToggle(sec.key)}
                                disabled={isGenerating}
                                size="small"
                              />
                              <span>{sec.label}</span>
                            </label>
                            {module === "analysis" && selectedSections[sec.key] && renderVisualMetricSelector(sec.key)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={styles["add-to-report-modal__footer"]}>
              <Button disabled={isGenerating} onClick={onClose} className={styles["btn-cancel"]}>
                Cancel
              </Button>
              <Button
                ref={generateBtnRef}
                variant="contained"
                disabled={!hasSelection || isGenerating || animatingReport || hasSubjectAreaViolation || hasEntitiesViolation}
                onClick={() => handleGenerate(exportFormat)}
                className={styles["btn-generate"]}
                startIcon={isGenerating && !animatingReport ? <Spin size="small" /> : null}
              >
                {isGenerating && !animatingReport ? "Requesting..." : (exportFormat === "xlsx" ? "Export XLSX" : "Generate PDF")}
              </Button>
            </div>

            {isGenerating && !animatingReport && (
              <div className={styles["loading-overlay"]}>
                <Spin size="large" />
                <p>Requesting generation...</p>
              </div>
            )}
          </div>
        </Grow>
      </Modal>

      {animatingReport && animStyle && (
        <div style={animStyle}>
          {exportFormat === "xlsx" ? (
            <TableView style={{ fontSize: 40, color: '#2e7d32' }} />
          ) : (
            <PictureAsPdf style={{ fontSize: 40, color: '#e53935' }} />
          )}
        </div>
      )}

      {contextHolder}
    </>
  );
};

export default AddToReportModal;
